import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Upload } from 'lucide-react'

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
})
type ProfileForm = z.infer<typeof profileSchema>

interface AdminProfile {
  first_name: string
  last_name: string
  email: string
  platform_role: string
}

const ROLE_LABELS: Record<string, string> = {
  super_admin:     'Platform Administrator',
  staff:           'CONORBIT Staff',
  support_agent:   'Support Agent',
  billing_manager: 'Billing Manager',
}

export function AdminProfileForm({
  adminProfile, onClose,
}: { adminProfile: AdminProfile | null | undefined; onClose: () => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  const form = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: adminProfile?.first_name ?? '',
      last_name: adminProfile?.last_name ?? '',
    },
  })

  const { data: avatarUrl } = useQuery({
    queryKey: ['admin-avatar'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await supabase
        .from('profiles')
        .select('avatar_url')
        .eq('user_id', user.id)
        .maybeSingle()
      return data?.avatar_url ?? null
    }
  })

  const initials = `${adminProfile?.first_name?.[0] ?? ''}${adminProfile?.last_name?.[0] ?? ''}`

  const mutation = useMutation({
    mutationFn: async (values: ProfileForm) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const fullName = `${values.first_name} ${values.last_name}`.trim()

      await Promise.all([
        supabase
          .from('app_admins' as any)
          .update({ first_name: values.first_name, last_name: values.last_name, full_name: fullName })
          .eq('user_id', user.id),
        supabase
          .from('profiles')
          .update({ full_name: fullName })
          .eq('user_id', user.id),
      ])
    },
    onSuccess: () => {
      toast({ title: 'Profile updated.' })
      queryClient.invalidateQueries({ queryKey: ['admin-identity'] })
      onClose()
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    }
  })

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please upload an image file', variant: 'destructive' })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Please upload an image smaller than 5MB', variant: 'destructive' })
      return
    }
    setUploadingAvatar(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const fileExt = file.name.split('.').pop()
      const path = `admin/${user.id}/${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true })
      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path)

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('user_id', user.id)
      if (updateError) throw updateError

      queryClient.invalidateQueries({ queryKey: ['admin-avatar'] })
      toast({ title: 'Avatar updated.' })
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' })
    } finally {
      setUploadingAvatar(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(v => mutation.mutate(v))} className="mt-6 space-y-5">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <AvatarImage src={avatarUrl ?? undefined} />
            <AvatarFallback className="bg-[#C8A84B] text-[#0D2240] font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarUpload}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={uploadingAvatar}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingAvatar ? 'Uploading…' : 'Change Avatar'}
            </Button>
          </div>
        </div>

        <FormField
          control={form.control}
          name="first_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl><Input {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="last_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl><Input {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input value={adminProfile?.email ?? ''} readOnly disabled className="bg-muted" />
        </div>

        <div className="space-y-1.5">
          <Label>Platform Role</Label>
          <div>
            <Badge variant="secondary">
              {ROLE_LABELS[adminProfile?.platform_role ?? ''] ?? adminProfile?.platform_role ?? ''}
            </Badge>
          </div>
          <p className="text-justify text-xs text-muted-foreground">
            Platform role cannot be self-modified. Contact a Platform Administrator to change it.
          </p>
        </div>

        <Button type="submit" className="w-full" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save Changes
        </Button>
      </form>
    </Form>
  )
}
